import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Server, 
  CreditCard, 
  Upload,
  Check,
  X,
  Image,
  Palette
} from 'lucide-react';
import api from '../../axios';

const SystemSettings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);

  const [formData, setFormData] = useState({
    // General Settings
    siteName: '',
    siteDescription: '',
    logo: null,
    favicon: null,
    primaryColor: '#3b82f6',
    secondaryColor: '#6366f1',
    // System Settings
    allowRegister: true,
    allowLogin: true,
    maintenanceMode: false,
    allowProjectUpload: true,
    allowIFCUpload: true,
    allowPDFUpload: true,
    // Fee and Payment Gateway
    processingFee: 0,
    approvalFee: 0,
    enableProcessingFee: true,
    enableApprovalFee: true,
    paymentGateway: 'stripe',
    apiKey: '',
    secretKey: ''
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files.length > 0) {
      setFormData({
        ...formData,
        [name]: files[0]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
  
    const form = new FormData();
    form.append('site_name', formData.siteName);
    form.append('site_description', formData.siteDescription);
    form.append('primary_color', formData.primaryColor);
    form.append('secondary_color', formData.secondaryColor);
    
   

    form.append('allow_register', formData.allowRegister ? '1' : '0');
    form.append('allow_login', formData.allowLogin ? '1' : '0');
    form.append('maintenance_mode', formData.maintenanceMode ? '1' : '0');
    form.append('allow_project_upload', formData.allowProjectUpload ? '1' : '0');
    form.append('allow_ifc_upload', formData.allowIFCUpload ? '1' : '0');
    form.append('allow_pdf_upload', formData.allowPDFUpload ? '1' : '0');

    form.append('enable_processing_fee', formData.enableProcessingFee ? '1' : '0');
    form.append('enable_approval_fee', formData.enableApprovalFee ? '1' : '0');

    
    form.append('processing_fee', formData.processingFee);
    form.append('approval_fee', formData.approvalFee);
    form.append('payment_gateway', formData.paymentGateway);
    form.append('api_key', formData.apiKey);
    form.append('secret_key', formData.secretKey);
    
  
    if (formData.logo) form.append('logo', formData.logo);
    if (formData.favicon) form.append('favicon', formData.favicon);
  
    try {
      const res = await api.post('/admin/settings/update', form, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
  
      setMessage(res.data.message || 'Settings saved successfully!');
      console.log('Server response:', res.data);
  
      // Optionally reset loading or show a toast
    } catch (err) {
      console.error('Error updating settings:', err);
      setMessage(
        err.response?.data?.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/admin/settings');
        const data = res.data.settings;
  
        setFormData({
          siteName: data.site_name || '',
          siteDescription: data.site_description || '',
          primaryColor: data.primary_color || '#3b82f6',
          secondaryColor: data.secondary_color || '#6366f1',
  
          allowRegister: !!data.allow_register,
          allowLogin: !!data.allow_login,
          maintenanceMode: !!data.maintenance_mode,
          allowProjectUpload: !!data.allow_project_upload,
          allowIFCUpload: !!data.allow_ifc_upload,
          allowPDFUpload: !!data.allow_pdf_upload,
  
          processingFee: data.processing_fee || 0,
          approvalFee: data.approval_fee || 0,
          enableProcessingFee: !!data.enable_processing_fee,
          enableApprovalFee: !!data.enable_approval_fee,
          paymentGateway: data.payment_gateway || 'stripe',
          apiKey: data.api_key || '',
          secretKey: data.secret_key || '',
  
          logo: null, // we keep this null so user can still upload a new file
          favicon: null
        });
  
        // Optional: set preview URLs
        if (data.logo_path) setLogoPreview(`${import.meta.env.VITE_BACKEND_URL}/storage/${data.logo_path}`);
        if (data.favicon_path) setFaviconPreview(`${import.meta.env.VITE_BACKEND_URL}/storage/${data.favicon_path}`);
  
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
  
    fetchSettings();
  }, []);
  

  const tabs = [
    { id: 'general', label: 'General Settings', icon: <Settings size={20} /> },
    { id: 'system', label: 'System Settings', icon: <Server size={20} /> },
    { id: 'payment', label: 'Fee & Payment Gateway', icon: <CreditCard size={20} /> }
  ];

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        
        
        {/* Tabs */}
        <div className="flex border-b">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
        {message && (
        <div className="text-green-600 text-sm font-medium mb-4">{message}</div>
        )}
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">General Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="siteName" className="block text-sm font-medium text-gray-700">
                    Site Name
                  </label>
                  <input
                    type="text"
                    id="siteName"
                    name="siteName"
                    value={formData.siteName}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your site name"
                  />
                </div>
                
                <div className="space-y-2 md:col-span-2">
                  <label htmlFor="siteDescription" className="block text-sm font-medium text-gray-700">
                    Site Description
                  </label>
                  <textarea
                    id="siteDescription"
                    name="siteDescription"
                    value={formData.siteDescription}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of your site"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Logo Upload
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="relative flex-1 p-4 border border-dashed border-gray-300 rounded-md">
                    {logoPreview && (
                        <img src={logoPreview} alt="Logo Preview" className="h-12 mt-2" />
                        )}

                        <div className="flex flex-col items-center justify-center space-y-2 pointer-events-none">
                            <Image size={24} className="text-gray-400" />
                            <span className="text-sm text-gray-500">
                            {formData.logo ? formData.logo.name : "Drop logo or click to upload"}
                            </span>
                        </div>
                        <input
                            type="file"
                            name="logo"
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        </div>

                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Favicon Upload
                  </label>
                  <div className="flex items-center space-x-2">
                    <div className="relative flex-1 p-4 border border-dashed border-gray-300 rounded-md">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <Image size={24} className="text-gray-400" />
                        <span className="text-sm text-gray-500">
                          {formData.favicon ? formData.favicon.name : "Drop favicon or click to upload"}
                        </span>
                        <input
                          type="file"
                          name="favicon"
                          onChange={handleFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700 flex items-center">
                    <Palette size={16} className="mr-1" /> Brand Color Primary
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      id="primaryColor"
                      name="primaryColor"
                      value={formData.primaryColor}
                      onChange={handleInputChange}
                      className="w-10 h-10 rounded border border-gray-300"
                    />
                    <input
                      type="text"
                      value={formData.primaryColor}
                      onChange={handleInputChange}
                      name="primaryColor"
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="secondaryColor" className="block text-sm font-medium text-gray-700 flex items-center">
                    <Palette size={16} className="mr-1" /> Brand Color Secondary
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      id="secondaryColor"
                      name="secondaryColor"
                      value={formData.secondaryColor}
                      onChange={handleInputChange}
                      className="w-10 h-10 rounded border border-gray-300"
                    />
                    <input
                      type="text"
                      value={formData.secondaryColor}
                      onChange={handleInputChange}
                      name="secondaryColor"
                      className="w-full p-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* System Settings */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">System Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-800">Allow Registration</h3>
                    <p className="text-sm text-gray-500">Enable or disable user registration</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="allowRegister"
                      checked={formData.allowRegister}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-800">Allow Login</h3>
                    <p className="text-sm text-gray-500">Enable or disable user login</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="allowLogin"
                      checked={formData.allowLogin}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-800">Maintenance Mode</h3>
                    <p className="text-sm text-gray-500">Put site in maintenance mode</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="maintenanceMode"
                      checked={formData.maintenanceMode}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-800">Project Upload</h3>
                    <p className="text-sm text-gray-500">Allow users to upload projects</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="allowProjectUpload"
                      checked={formData.allowProjectUpload}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-800">IFC File Upload</h3>
                    <p className="text-sm text-gray-500">Allow users to upload IFC files</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="allowIFCUpload"
                      checked={formData.allowIFCUpload}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-800">PDF Upload</h3>
                    <p className="text-sm text-gray-500">Allow users to upload PDF files</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="allowPDFUpload"
                      checked={formData.allowPDFUpload}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>
          )}
          
          {/* Fee and Payment Gateway */}
          {activeTab === 'payment' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">Fee and Payment Gateway</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-800">Processing Fee</h3>
                      <p className="text-sm text-gray-500">Fee charged for processing</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="enableProcessingFee"
                        checked={formData.enableProcessingFee}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <span className="text-lg font-medium text-gray-700 mr-2">$</span>
                    <input
                      type="number"
                      name="processingFee"
                      value={formData.processingFee}
                      onChange={handleInputChange}
                      disabled={!formData.enableProcessingFee}
                      className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-800">Approval Fee</h3>
                      <p className="text-sm text-gray-500">Fee charged for approval</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        name="enableApprovalFee"
                        checked={formData.enableApprovalFee}
                        onChange={handleInputChange}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <span className="text-lg font-medium text-gray-700 mr-2">$</span>
                    <input
                      type="number"
                      name="approvalFee"
                      value={formData.approvalFee}
                      onChange={handleInputChange}
                      disabled={!formData.enableApprovalFee}
                      className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                <div className="md:col-span-2 space-y-2">
                  <label htmlFor="paymentGateway" className="block text-sm font-medium text-gray-700">
                    Payment Gateway
                  </label>
                  <select
                    id="paymentGateway"
                    name="paymentGateway"
                    value={formData.paymentGateway}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="stripe">Stripe</option>
                    <option value="paypal">PayPal</option>
                    <option value="razorpay">Razorpay</option>
                    <option value="square">Square</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700">
                    API Key
                  </label>
                  <input
                    type="password"
                    id="apiKey"
                    name="apiKey"
                    value={formData.apiKey}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter API key"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="secretKey" className="block text-sm font-medium text-gray-700">
                    Secret Key
                  </label>
                  <input
                    type="password"
                    id="secretKey"
                    name="secretKey"
                    value={formData.secretKey}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter secret key"
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Form Actions */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end space-x-4">

            <button
                type="button"
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
            >
                Cancel
            </button>

            <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                ${loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center`}
            >
                {loading ? (
                <>
                    <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Saving...
                </>
                ) : (
                'Save Changes'
                )}
            </button>

          </div>
        </form>
      </div>
    </div>
  );
}


export default  SystemSettings;


  